import {
  DROPDOWN_OPTIONS,
  LIEN_LIST_DATA,
} from '@pages/DocumentAuditPage/modules/DocumentTaskDetail/components/DocumentReview/DocumentForms/ApproveForm/constants';
import { formatPrice } from '@shared/utils';
import { formatAccessoryAreaSuggestionForDisplay } from './accessoryAreas';

export const FIELD_LABELS_MX: Record<string, string> = {
  complex: 'Conjunto',
  propertyType: 'Tipo de Inmueble',
  adminPrice: 'Valor admin.',
  medianZone: 'Zona mediana',
  zipCode: 'Código postal',
  documentType: 'Tipo de documento para determinar el área',
  committeePriceArea: 'Área precio comité',
  committeeLandArea: 'Área terreno comité',
  committeeExpansionArea: 'Comité área de ampliación',
  rooms: 'Recamaras',
  baths: 'Baños',
  halfBathroom: 'Medios baños',
  floor: 'Piso',
  elevator: 'Elevador',
  hasGarageDoc: 'Documento estacio.',
  garage: 'Estacionamiento',
  parkingWithDoubleKeyOrDeed: 'Estacionamiento con doble clave o escritura',
  yearsOld: 'Antigüedad',
  accessoryAreas: 'Áreas accesorias',
};

export const FIELD_LABELS_CO: Record<string, string> = {
  hasAdmin: 'Recibo admin.',
  adminPrice: 'Valor admin.',
  constructionDate: 'Fecha construcción del inmueble',
  yearsOld: 'Antigüedad',
  areaType: 'Tipo de área',
  area: 'Área',
  baths: 'Baños',
  floor: 'Piso',
  hasGarage: 'Garaje',
  garage: 'Cantidad de garajes',
  hasTerrace: 'Balcón/Terraza (opc)',
  freeArea: 'Área libre (opc)',
  liens: 'Gravámenes',
  lenderBank: 'Banco hipoteca (opcional)',
  liensCategory: 'Categoría de gravamen',
  propertyType: 'Tipo de Inmueble',
  elevators: 'Elevadores',
};

export const getFieldLabelForCountry = (
  country: 'MX' | 'CO' | undefined,
  field: string
): string => {
  if (country === 'MX') {
    return FIELD_LABELS_MX[field] ?? field;
  }
  if (country === 'CO') {
    return FIELD_LABELS_CO[field] ?? field;
  }
  return field;
};

export const SOURCE_TYPE_LABELS: Record<string, string> = {
  document: 'Documento',
  video: 'Video',
  audio: 'Audio',
  image: 'Imagen',
  database: 'Base de datos',
  manual: 'Manual',
  hubspot: 'Hubspot',
  api: 'API',
};

export const SOURCE_LABELS: Record<
  string,
  { label: string; className: string }
> = {
  ctl: { label: 'CTL', className: 'badge-ctl' },
  deed: { label: 'Escritura', className: 'badge-deed' },
  admin_receipt: { label: 'Recibo administración', className: 'badge-admin' },
  hubspot: { label: 'Hubspot', className: 'badge-hubspot' },
  beepquest: { label: 'Beepquest', className: 'badge-beepquest' },
  appraisal: { label: 'Avaluos', className: 'badge-appraisal' },
  polynator: { label: 'Polynator', className: 'badge-polynator' },
  bigquery: { label: 'BigQuery', className: 'badge-bigquery' },
  sig: { label: 'SIG', className: 'badge-sig' },
  google_maps: { label: 'Google Maps', className: 'badge-google-maps' },
  report_and_remo_measurement: { label: 'Reporte y medición Remo', className: 'badge-report-remo' },
  georeference: { label: 'Georreferencia', className: 'badge-georeference' },
  my_maps_extractor: { label: 'My Maps Extractor', className: 'badge-my-maps' },
  remo_measurement: { label: 'Medición Remo', className: 'badge-remo-measurement' },
  pricing: { label: 'Pricing', className: 'badge-pricing' },
  video_vo: { label: 'Video visita operativa', className: 'badge-video-vo' },
  agent_automatic: { label: 'Agente automático', className: 'badge-agent-automatic' },
  property_tax: { label: 'Boleta predial', className: 'badge-property-tax' },
};

export const MODEL_LABELS: Record<string, string> = {
  gemini: 'Gemini',
  hubspot: 'Hubspot',
  beepquest: 'Beepquest',
  appraisal: 'Avaluos',
  polynator: 'Polynator',
  bigquery: 'BigQuery',
  sig: 'SIG',
  google_maps: 'Google Maps',
  report_and_remo_measurement: 'Reporte y medición Remo',
  georeference: 'Georreferencia',
  my_maps_extractor: 'My Maps Extractor',
  remo_measurement: 'Medición Remo',
  pricing: 'Pricing',
  video_vo: 'Video visita operativa',
};

export const SOURCE_TO_TITLE: Record<string, string> = {
  ctl: 'Documento CTL',
  deed: 'Documento Escritura',
  admin_receipt: 'Recibo administración',
  property_tax: 'Boleta predial',
};

// Field value resolvers
const resolveLiens = (val: string): string => {
  const found = LIEN_LIST_DATA.find(([, v]) => v === val);
  return found ? found[0] : val;
};

const resolveAreaType = (val: string): string => {
  const found = DROPDOWN_OPTIONS.AREA_TYPE.find(item => item.value === val);
  return found ? found.label : val;
};

const resolveAdminPrice = (val: string): string => {
  const num = Number(val);
  return isNaN(num) ? val : formatPrice(num);
};

const resolveHasTerrace = (val: string): string => {
  if (val === 'true') return 'Sí';
  if (val === 'false') return 'No';
  return val;
};

const resolveHasAdmin = (val: string): string => {
  if (val === 'Si') return 'Sí';
  if (val === 'No') return 'No';
  return val;
};

const resolveNumericField = (val: string): string => {
  const num = Number(val);
  return isNaN(num) ? val : Math.round(num).toString();
};

const resolvePropertyType = (val: string): string => {
  const foundMX = DROPDOWN_OPTIONS.PROPERTY_TYPE.find(item => item.value === val);
  if (foundMX) return foundMX.label;
  const foundCO = DROPDOWN_OPTIONS.PROPERTY_TYPE_CO.find(item => item.value === val);
  return foundCO ? foundCO.label : val;
};

const resolveParkingWithDoubleKeyOrDeed = (val: string): string => {
  if (val === 'true' || val === '1') return 'Sí';
  if (val === 'false' || val === '0') return 'No';
  return val;
};

const resolveLienCategory = (val: string): string => {
  const found = DROPDOWN_OPTIONS.LIEN_CATEGORY.find(item => item.value === val);
  return found ? found.label : val;
};

const resolveAccessoryAreas = (val: string): string =>
  formatAccessoryAreaSuggestionForDisplay(val);

// Map of field resolvers
const FIELD_RESOLVERS: Record<string, (val: string) => string> = {
  liens: resolveLiens,
  areaType: resolveAreaType,
  adminPrice: resolveAdminPrice,
  hasTerrace: resolveHasTerrace,
  hasAdmin: resolveHasAdmin,
  elevator: resolveNumericField,
  yearsOld: resolveNumericField,
  propertyType: resolvePropertyType,
  parkingWithDoubleKeyOrDeed: resolveParkingWithDoubleKeyOrDeed,
  liensCategory: resolveLienCategory,
  accessoryAreas: resolveAccessoryAreas,
};

export const getFieldLabel = (field: string, value: string): string => {
  if (!value || value.trim() === '') {
    return 'Sin sugerencia';
  }

  if (field === 'accessoryAreas') {
    return resolveAccessoryAreas(value);
  }

  const resolveSingleValue = (val: string): string => {
    const resolver = FIELD_RESOLVERS[field];
    return resolver ? resolver(val) : val;
  };

  // Handle multiple values separated by comma or semicolon
  if (value.includes(',') || value.includes(';')) {
    const separator = value.includes(';') ? ';' : ',';
    return value
      .split(separator)
      .map(v => resolveSingleValue(v.trim()))
      .join(', ');
  }

  return resolveSingleValue(value);
};
